import { computed, ref } from '@vue/composition-api';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import VacancySettings from '@/orm/models/Vacancy/VacancySettings';
import Wizard from '@/orm/models/Wizard/Wizard';
import useValidation from '@/compositions/useValidation';
import User from '@/orm/models/User/User';

const currentVacancy = ref(null);

export default function useVacancies() {
  const {
    withApiErrorHandling,
    localLoading,
  } = useValidation();

  function publishVacancy(vacancyId, onPublish, onFailure) {
    return withApiErrorHandling(async () => {
      await Wizard.submitQuestions();
      await Vacancy.api()
        .activate(vacancyId);
      onPublish();
    }, (e) => {
      onFailure(e);
    }, 'publishLoading');
  }

  function saveVacancyDraft(onSave) {
    Wizard.submitQuestions()
      .then(() => {
        onSave();
      });
  }

  function getVacancyById(vacancyId) {
    return Vacancy.query()
      .withAllRecursive()
      .find(vacancyId);
  }

  const vacancies = computed(() => {
    return Vacancy.query()
      .withAllRecursive()
      .all();
  });

  const fetch = async () => {
    const authUser = User.getAuthUser();
    await Vacancy.api()
      .fetchAllByCompanyId(authUser.company.id);
  };

  async function fetchById(id) {
    await Vacancy.api().fetchById(id);
  }

  async function copyCandidates(id, payload) {
    await Vacancy.api().copyCandidates(id, payload);
  }

  async function publishAfterAiCompile(id) {
    await Vacancy.api().publishAfterAiCompile(id);
  }

  async function activate(id) {
    await Vacancy.api().activate(id);
  }

  return {
    localLoading,
    publishVacancy,
    saveVacancyDraft,
    getVacancyById,
    copyCandidates,
    activeVacancies: computed(() => {
      return Vacancy.scopes.activeVacancies()
        .all();
    }),
    notArchivedNotDraftVacancies: computed(() => {
      return Vacancy.scopes.notArchivedNotDraftVacancies()
        .all();
    }),
    currentVacancy,
    notArchivedVacancies: computed(() => {
      return Vacancy.scopes.notArchivedVacancies()
        .with('address')
        .all();
    }),
    hasIntegrated: computed(() => {
      return currentVacancy.value?.externalVacancies?.length > 0;
    }),
    async updateSettings(id, settings) {
      await VacancySettings.api()
        .update(id, settings);
    },
    findVacancyById(id) {
      return Vacancy.query()
        .withAllRecursive()
        .find(id);
    },
    vacancies,
    fetch,
    fetchById,
    publishAfterAiCompile,
    activate,
  };
}
